import type { OnboardingStage } from "@/domain/payment-account/rules";
import { formatDateTime } from "@/lib/time";

/** „Účet je zřízen" and „můžete prodávat" are two different facts (lastgo-api
 *  ADR 0004), so every stage that already has an account says so before it says
 *  what is still missing. */
const stageBody: Record<OnboardingStage, string> = {
  "no-account":
    "Založte platební účet a dokončete ověření u Stripe. Dokud není hotové, zákazníci vaše slevové termíny nevidí.",
  unknown:
    "Platební účet máte zřízený, jeho stav u Stripe se ale teď nedaří zjistit. Zkuste ho načíst znovu v nastavení podniku.",
  verifying:
    "Platební účet máte zřízený a Stripe teď údaje ověřuje. Nemusíte nic dělat — jakmile ověření skončí, zákazníci vaše slevové termíny uvidí.",
  "action-needed":
    "Platební účet máte zřízený, Stripe ale čeká na doplnění údajů. Dokud ověření nedokončíte, zákazníci vaše slevové termíny nevidí.",
  restricted:
    "Platební účet máte zřízený, Stripe u něj ale omezil část plateb. Důvod najdete v nastavení podniku a řeší se přímo ve Stripe.",
  "capability-unavailable":
    "Platební účet máte zřízený, chybí mu ale oprávnění, které ověření nedoplní. Ozvěte se nám — účet je potřeba zřídit znovu.",
  tradeable: "Platební účet je ověřený a můžete prodávat.",
};

/** A stage the last Stripe read failed to refresh is a memory, not an answer,
 *  and the settings card already says so in these words — a partner who reads
 *  both screens must not find „Stripe teď ověřuje" on one and „naposledy" on
 *  the other. `unknown` needs no prefix: it means no read ever succeeded, which
 *  its own body says. */
export function paymentAccountStepBody(stage: OnboardingStage, rememberedFrom: Date | null): string {
  if (rememberedFrom === null) return stageBody[stage];
  return `Se Stripe se teď nedaří spojit, takže vidíte stav z ${formatDateTime(rememberedFrom)}. ${stageBody[stage]}`;
}

export const firstStepsCopy = {
  paymentAccount: {
    title: "Platební účet",
    cta: "Přejít do nastavení podniku",
  },
  activity: {
    title: "První šablona slev",
    body: "Řekněte, co váš podnik nabízí. Z šablony pak vypisujete jednotlivé termíny se slevou.",
    cta: "Přejít na šablony slev",
  },
  offer: {
    title: "První slevový termín",
    body: "Vypište termín se slevou. Připravit si ho můžete hned — zákazníkům se ukáže, jakmile bude účet ověřený.",
    cta: "Přejít na slevové termíny",
  },
} as const;
